import { cn } from '@/lib/utils'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import * as React from 'react'

function Root({
    className,
    ...props
}: Omit<AccordionPrimitive.AccordionSingleProps, 'type'>) {
    return (
        <AccordionPrimitive.Root
            data-slot="accordion"
            className={cn(
                'rounded-lg shadow-md ring-1 ring-accent overflow-hidden',
                'bg-card',
                'first:[&_div]:border-t-md',
                className,
            )}
            type="single"
            {...props}
        />
    )
}

function Item({
    className,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
    return (
        <AccordionPrimitive.Item
            data-slot="accordion-item"
            className={cn('pb-2 border-b last:border-b-0', className)}
            {...props}
        />
    )
}

function Trigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
    const [isOpen, setIsOpen] = React.useState(false)

    return (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                data-slot="accordion-trigger"
                className={cn(
                    'focus-visible:border-ring focus-visible:ring-ring/50',
                    'flex flex-1 items-center justify-start',
                    'gap-4 text-left text-sm font-medium',
                    'transition-all outline-none',
                    'hover:cursor-pointer',
                    'focus-visible:ring-[3px]',
                    'disabled:pointer-events-none disabled:opacity-50',
                    'text-secondary-foreground p-4 border-0',
                    'bg-inherit hover:bg-primary/10',
                    className,
                )}
                ref={el => {
                    if (el) {
                        const observer = new MutationObserver(() => {
                            setIsOpen(el.getAttribute('data-state') === 'open')
                        })
                        observer.observe(el, {
                            attributes: true,
                            attributeFilter: ['data-state'],
                        })
                        setIsOpen(el.getAttribute('data-state') === 'open')
                    }
                }}
                {...props}
            >
                <div
                    className={cn(
                        'size-4 rounded-full border-2 flex items-center justify-center transition-colors',
                        {
                            'border-primary border-[2px]': isOpen,
                            'border-muted-foreground': !isOpen,
                        },
                    )}
                >
                    {isOpen && (
                        <div className="size-2 rounded-full bg-primary" />
                    )}
                </div>
                {children}
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    )
}

function Content({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
    return (
        <AccordionPrimitive.Content
            data-slot="accordion-content"
            className={cn(
                'data-[state=closed]:animate-accordion-up',
                'data-[state=open]:animate-accordion-down',
                'overflow-hidden text-sm',
                'p-4',
            )}
            {...props}
        >
            <div className={cn('pt-0 pb-4', className)}>{children}</div>
        </AccordionPrimitive.Content>
    )
}

/** Accordion no estilo Radio
 *
 * @example
 * ```tsx
 * <RadioAccordion.Root type="single">
 *   <RadioAccordion.Item value="item-1">
 *     <RadioAccordion.Trigger>Item 1</RadioAccordion.Trigger>
 *     <RadioAccordion.Content>Conteúdo do Item 1</RadioAccordion.Content>
 *   </RadioAccordion.Item>
 *   <RadioAccordion.Item value="item-2">
 *     <RadioAccordion.Trigger>Item 2</RadioAccordion.Trigger>
 *     <RadioAccordion.Content>Conteúdo do Item 2</RadioAccordion.Content>
 *   </RadioAccordion.Item>
 * </RadioAccordion.Root>
 * ```
 */
export const RadioAccordion = {
    Root,
    Item,
    Trigger,
    Content,
}
